/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { clone, find, isEmpty, toString } from 'es-toolkit/compat';
import { oc } from 'ts-optchain';

import clsx from 'clsx';

import BaseComponent from '../BaseComponent';
import IconChevronSmallDown from '../../icons/IconChevronSmallDown';
import Input from './Input';
import InputContainer from '../forms/InputContainer';

interface Props<T> {
  autoComplete?: string;
  'data-testid'?: string;
  disabled?: boolean;
  field: ReduxFormField<T>;
  id?: string;
  modifierClassName?: string;
  onBlur?(): void;
  /**
   * A low-level handler for reacting to selection change, which calls back
   * in terms of the underlying synthesized event. It can be specified
   * alongside onChangeSelectOption, in which case both handlers will fire. (Do
   * not rely on any ordering between those handlers firing.)
   */
  onChange?(event: React.ChangeEvent<HTMLInputElement>): void;
  /**
   * A higher-level handler for reacting to selection change, which calls back
   * in terms of the SelectOption config corresponding to the <option> the
   * user has changed to. It can be specified alongside onChange, in which case
   * both handlers will fire. (Do not rely on any ordering between those handlers
   * firing.)
   */
  onChangeSelectOption?(option: SelectOption): void;
  options: SelectOption[];
  placeholder?: string;
  ariaLabel?: string;
  showErrorOnUntouchedField?: boolean;
}

/**
 * @deprecated
 * Please see the inputs in the ui folder of your project
 */
export default class Select<T = string | number> extends BaseComponent<Props<T>> {
  static defaultProps = {
    modifierClassName: 'Select--default',
    disabled: false,
  };

  getStringToDisplay(option?: SelectOption): string {
    return oc(option).display() || oc(option).label() || '';
  }

  render() {
    const {
      disabled,
      field,
      modifierClassName,
      onChange,
      onChangeSelectOption,
      options,
      placeholder,
      showErrorOnUntouchedField,
    } = this.props;
    const decoratedOptions = clone(options);

    if (!isEmpty(placeholder)) {
      const placeholderOption: SelectOption = {
        key: 'placeholder',
        value: '',
        label: placeholder as string,
      };

      decoratedOptions.unshift(placeholderOption);
    }

    // NOTE: Undefined does weird things with redux-form, treat an Undefined
    // form field as an empty string so we can assign the placeholder option a
    // value.
    const fieldValue = (field.value !== undefined) ? field.value : ''; // value type is number | string

    const selectedOption = find(decoratedOptions, (option) => {
      return toString(option.value) === toString(fieldValue);
    });

    const hasError = showErrorOnUntouchedField
      ? field && field.error
      : field && field.touched && field.error;

    const className = clsx('Select', modifierClassName, {
      'is-disabled': disabled,
    });

    const textClassNames = clsx('Select-text', {
      'is-placeholder': fieldValue === '',
    });

    const indicatorClassName = clsx('Select-indicator', {
      'has-errors': hasError,
    });

    const effectiveOnChange = onChangeSelectOption
      ? (event: React.ChangeEvent<HTMLInputElement>) => {
          const { value } = event.target;
          for (const option of options) {
            if (option.value === value) {
              onChangeSelectOption(option);
              break;
            }
          }
          onChange?.(event);
        }
      : onChange;

    return (
      <InputContainer className={className} disabled={disabled}>
        <Input
          {...this.props}
          onChange={effectiveOnChange}
          data-testid={this.props['data-testid']}
          classNames="Select-input"
          options={decoratedOptions}
          type="select"
          value={fieldValue as any}
        />

        <div className={indicatorClassName}>
          <div className={textClassNames} data-testid="Select-text">
            {this.getStringToDisplay(selectedOption)}
          </div>
          <IconChevronSmallDown />
        </div>
      </InputContainer>
    );
  }
}
